export interface LanguageSelectItem {
    id: string;
    children: string;
}

/**
 * Builds the language options for the Roadside Check dropdown.
 * Always includes English; adds the driver's profile language and
 * Canadian French when in a Canadian jurisdiction, if not already present.
 * Result is deduplicated and capped at 3 items.
 *
 * @param driverLanguage - the driver's profile language code (e.g. "fr")
 * @param isCanada - whether the driver is operating in a Canadian jurisdiction
 * @param getLanguageName - function that resolves a language code to its display name
 */
export const getRoadsideCheckLanguageOptions = (
    driverLanguage: string,
    isCanada: boolean,
    getLanguageName: (code: string) => string
): LanguageSelectItem[] => {
    const seen = new Set<string>();
    const options: LanguageSelectItem[] = [];

    const add = (code: string) => {
        if (!seen.has(code)) {
            seen.add(code);
            options.push({ id: code, children: getLanguageName(code) });
        }
    };

    add("en");
    if (driverLanguage && driverLanguage !== "en") {
        add(driverLanguage);
    }
    if (isCanada && !seen.has("fr")) {
        add("fr");
    }

    return options;
};

const roadsideCheckLanguageNames: Record<string, string> = {
    "en": "English",
    "fr": "Français (Canada)",
    "es": "Español (Latam)",
    "es-419": "Español (Latam)",
    "ja": "日本語",
    "de": "Deutsch",
    "pl": "Polski",
    "pt-br": "Português (BR)",
    "nl": "Nederlands",
    "it": "Italiano",
    "zh-hans": "简体中文",
    "th": "ไทย",
    "id": "Bahasa Indonesia",
    "cs": "Čeština",
    "sv": "Svenska",
    "tr": "Türkçe",
    "ms": "Bahasa Malay",
    "fr-fr": "Français (France)",
    "ko-kr": "한국어",
    "zh-tw": "繁體中文",
    "da-dk": "Dansk",
    "fi-fi": "Suomi",
    "hu-hu": "Magyar",
    "nb-no": "Norwegian Bokmål",
    "sk-sk": "Slovenčina",
    "pt-pt": "Português (Portugal)",
    "ro-ro": "Română",
    "es-es": "Español (España)",
    "bg-bg": "Български",
    "el-gr": "Ελληνικά",
    "et-ee": "Eesti keel",
    "ka-ge": "ქართული",
    "lt-lt": "Lietuvių kalba",
    "lv-lv": "Latviešu valoda",
    "ar-001": "العربية",
    "ar": "العربية"
};

/**
 * Gets the Roadside Check-specific language name used by the language selector.
 * These names intentionally match the pre-existing top-bar selector labels.
 */
export const getRoadsideCheckLanguageName = (language: string): string => {
    return roadsideCheckLanguageNames[language.toLowerCase()] || "English";
};
